import React, { useEffect, useRef, useState } from "react";
import type { User } from "../shared/contracts.ts";
import {
  compare,
  displayFraction,
  fraction,
  multiply,
  type Fraction,
} from "../shared/progress.ts";
import { api, Banner, Button, Field } from "./ui.tsx";
import "./progress.css";
import { ExactCount } from "./exact-count.tsx";
type GoalRow = {
  goal: {
    id: string;
    name: string;
    kind: string;
    indicator: string;
    target: string;
    points: string;
    xp: string;
    state: string;
    deadline: string;
    achievement: string;
    version: number;
  };
  displayState: string;
  calculation: {
    progress: Fraction;
    contributions: Record<string, Fraction>;
    totalContribution: Fraction;
  };
  participants: Array<{ id: string; name: string }>;
  own: {
    userId: string;
    contribution: Fraction;
    share: Fraction | null;
    reward: { points: Fraction; xp: Fraction } | null;
  };
};
const states: Record<string, string> = {
  active: "Em andamento",
  paused: "Pausada",
  achieved: "Atingida",
  cancelled: "Cancelada",
  expired: "Prazo encerrado",
};
const percentage = (n: Fraction, d: Fraction) =>
  BigInt(d.n) === 0n
    ? null
    : multiply(n, fraction(BigInt(d.d) * 100n, BigInt(d.n)));
export function Goals({
  user,
  notify,
}: {
  user: User;
  notify: (s: string) => void;
}) {
  const [rows, setRows] = useState<GoalRow[]>([]),
    [digits, setDigits] = useState(2),
    [loading, setLoading] = useState(true),
    [error, setError] = useState(""),
    [creating, setCreating] = useState(false),
    [selected, setSelected] = useState<string | null>(null),
    [finished, setFinished] = useState(false),
    [retry, setRetry] = useState(0);
  const previousScroll = useRef(0);
  useEffect(() => {
    const abort = new AbortController();
    setLoading(true);
    setError("");
    api<{ items: GoalRow[]; digits: number }>(
      "/goals",
      "GET",
      undefined,
      abort.signal,
    )
      .then((r) => {
        if (!abort.signal.aborted) {
          setRows(r.items);
          setDigits(r.digits);
        }
      })
      .catch((e) => {
        if (!abort.signal.aborted) setError(e.message);
      })
      .finally(() => {
        if (!abort.signal.aborted) setLoading(false);
      });
    return () => abort.abort();
  }, [retry, user.id]);
  const selectedGoal = rows.find((r) => r.goal.id === selected);
  async function change(row: GoalRow, state: string) {
    try {
      await api("/goals/" + row.goal.id, "PATCH", {
        version: row.goal.version,
        state,
      });
      notify("Meta atualizada.");
      setRetry((n) => n + 1);
    } catch (e) {
      setError((e as Error).message);
    }
  }
  if (selectedGoal) {
    const row = selectedGoal,
      g = row.goal;
    const contributors = row.participants
      .map((p) => ({
        ...p,
        value: row.calculation.contributions[p.id] ?? fraction(0n),
        rank: 0,
      }))
      .sort(
        (a, b) =>
          compare(b.value, a.value) || a.name.localeCompare(b.name, "pt-BR"),
      );
    const positive = contributors.filter((p) => BigInt(p.value.n) > 0n);
    let rank = 0;
    positive.forEach((p, i) => {
      if (i === 0 || compare(p.value, positive[i - 1].value) !== 0)
        rank = i + 1;
      p.rank = rank;
    });
    return (
      <section className="progress-page">
        <Button
          onClick={() => {
            setSelected(null);
            requestAnimationFrame(() =>
              window.scrollTo({ top: previousScroll.current }),
            );
          }}
        >
          Voltar às metas
        </Button>
        <p>{g.name}</p>
        <h1>Contribuições da equipe</h1>
        <p>
          Prazo: {new Date(g.deadline).toLocaleString("pt-BR")} ·{" "}
          {states[row.displayState]}
        </p>
        <PersonalGoal row={row} digits={digits} />
        <p>
          {displayFraction(
            row.calculation.progress,
            row.goal.indicator === "demands" ? 0 : digits,
          )}{" "}
          de {g.target}{" "}
          {g.indicator === "demands" ? "demandas" : "pontos elegíveis"}
        </p>
        {!positive.length ? (
          <Banner>Ninguém contribuiu para esta meta ainda.</Banner>
        ) : (
          <>
            <ol className="progress-podium">
              {positive.slice(0, 3).map((p, i) => (
                <li key={p.id} className={"podium-slot-" + i}>
                  {p.rank === 1 && (
                    <span className="progress-crown" aria-hidden="true">
                      ♛
                    </span>
                  )}
                  <span className="progress-avatar" aria-hidden="true">
                    {p.name
                      .split(" ")
                      .slice(0, 2)
                      .map((n) => n[0])
                      .join("")}
                  </span>
                  <strong>
                    {p.name}
                    {p.id === user.id ? " · Você" : ""}
                  </strong>
                  <span className="progress-number">
                    <ExactCount
                      value={p.value}
                      digits={digits}
                      scope={`goal:${g.id}:${user.id}:${p.id}`}
                      label={g.indicator === "demands" ? "demandas" : "pontos"}
                    />
                  </span>
                  <span className="muted">
                    {g.indicator === "demands" ? "demandas" : "pontos"} ·{" "}
                    {displayFraction(
                      percentage(p.value, row.calculation.totalContribution)!,
                      digits,
                    )}
                    %
                  </span>
                  <span className="progress-place">{p.rank}º</span>
                </li>
              ))}
            </ol>
            <ul className="progress-rest">
              {positive.slice(3).map((p, i) => (
                <li key={p.id}>
                  <span>{p.rank}º</span>
                  <div>
                    <strong>
                      {p.name}
                      {p.id === user.id ? " · Você" : ""}
                    </strong>
                    <span>
                      {displayFraction(p.value, digits)}{" "}
                      {g.indicator === "demands" ? "demandas" : "pontos"}
                    </span>
                    <small>
                      {displayFraction(
                        percentage(p.value, row.calculation.totalContribution)!,
                        digits,
                      )}
                      % da participação
                    </small>
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
        {!!contributors.filter((p) => BigInt(p.value.n) === 0n).length && (
          <details className="progress-card">
            <summary>Participantes sem contribuição</summary>
            <ul>
              {contributors
                .filter((p) => BigInt(p.value.n) === 0n)
                .map((p) => (
                  <li key={p.id}>
                    {p.name}
                    {p.id === user.id ? " · Você" : ""}
                  </li>
                ))}
            </ul>
          </details>
        )}
        <details className="progress-card">
          <summary>Como é calculado</summary>
          <p>
            Prêmio total: {g.points} pontos · {g.xp} XP. Demandas compartilhadas
            contam uma vez no total; a contribuição coletiva de cada
            participante é 1/n dos participantes originais. A divisão do prêmio
            é proporcional à contribuição elegível dos participantes desta meta.
          </p>
          <p>
            As frações são preservadas no cálculo. A apresentação usa {digits}{" "}
            casas decimais, sem alterar a distribuição. Ao atingir o objetivo, a
            divisão é congelada e registrada uma única vez. Dados de outras
            metas não entram nesta contribuição.
          </p>
        </details>
      </section>
    );
  }
  return (
    <section className="progress-page">
      <div className="progress-heading">
        <h1>Metas</h1>
        {user.admin && (
          <Button kind="primary" onClick={() => setCreating(!creating)}>
            {creating ? "Fechar cadastro" : "Criar meta"}
          </Button>
        )}
      </div>
      {error && (
        <Banner kind="danger">
          {error}
          <Button onClick={() => setRetry((n) => n + 1)}>
            Tentar novamente
          </Button>
        </Banner>
      )}
      {creating && (
        <GoalCreate
          onSaved={() => {
            setCreating(false);
            setRetry((n) => n + 1);
            notify("Meta criada.");
          }}
        />
      )}
      <div
        className="progress-tabs"
        role="tablist"
        aria-label="Situação das metas"
      >
        <button
          role="tab"
          aria-selected={!finished}
          onClick={() => setFinished(false)}
        >
          Em andamento
        </button>
        <button
          role="tab"
          aria-selected={finished}
          onClick={() => setFinished(true)}
        >
          Finalizadas
        </button>
      </div>
      {loading ? (
        <p role="status">Consultando metas…</p>
      ) : !rows.filter((r) =>
          finished
            ? !["active", "paused"].includes(r.displayState)
            : ["active", "paused"].includes(r.displayState),
        ).length ? (
        <p>Nenhuma meta nesta área.</p>
      ) : (
        rows
          .filter((r) =>
            finished
              ? !["active", "paused"].includes(r.displayState)
              : ["active", "paused"].includes(r.displayState),
          )
          .map((row) => (
            <article key={row.goal.id} className="progress-card">
              <div className="progress-heading">
                <h2>{row.goal.name}</h2>
                <span>{states[row.displayState]}</span>
              </div>
              <p>
                {displayFraction(
                  row.calculation.progress,
                  row.goal.indicator === "demands" ? 0 : digits,
                )}{" "}
                de {row.goal.target}{" "}
                {row.goal.indicator === "demands"
                  ? "demandas"
                  : "pontos elegíveis"}{" "}
                · até {new Date(row.goal.deadline).toLocaleDateString("pt-BR")}
              </p>
              <PersonalGoal row={row} digits={digits} />
              <div className="progress-actions">
                <Button
                  onClick={() => {
                    previousScroll.current = window.scrollY;
                    setSelected(row.goal.id);
                    window.scrollTo({ top: 0 });
                  }}
                >
                  Ver contribuições
                </Button>
                {user.admin &&
                  ["active", "paused"].includes(row.displayState) && (
                    <>
                      <Button
                        onClick={() =>
                          change(
                            row,
                            row.goal.state === "paused" ? "active" : "paused",
                          )
                        }
                      >
                        {row.goal.state === "paused" ? "Retomar" : "Pausar"}
                      </Button>
                      <Button
                        kind="danger-ghost"
                        onClick={() => {
                          if (
                            window.confirm(
                              "Cancelar esta meta? Ela deixará de receber contribuições.",
                            )
                          )
                            void change(row, "cancelled");
                        }}
                      >
                        Cancelar
                      </Button>
                    </>
                  )}
              </div>
            </article>
          ))
      )}
    </section>
  );
}
function PersonalGoal({ row, digits }: { row: GoalRow; digits: number }) {
  return (
    <div className="goal-personal">
      <p>
        <strong>Sua contribuição</strong>
        <br />
        {displayFraction(row.own.contribution, digits)}{" "}
        {row.goal.indicator === "demands" ? "demandas" : "pontos"}
        {row.own.share
          ? " · " +
            displayFraction(multiply(row.own.share, fraction(100n)), digits) +
            "%"
          : ""}
      </p>
      <p>
        <strong>
          {row.goal.state === "achieved"
            ? "Sua recompensa confirmada"
            : "Sua recompensa estimada"}
        </strong>
        <br />
        {row.own.reward
          ? `${displayFraction(row.own.reward.points, digits)} pontos · ${displayFraction(row.own.reward.xp, digits)} XP`
          : "Ainda sem contribuição elegível para calcular."}
      </p>
      {row.goal.state === "active" && (
        <small>A estimativa muda conforme a equipe contribui.</small>
      )}
    </div>
  );
}
function GoalCreate({ onSaved }: { onSaved: () => void }) {
  const [users, setUsers] = useState<Array<{ id: string; name: string }>>([]),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState({
    name: "",
    kind: "collective",
    indicator: "demands",
    participantIds: [] as string[],
    start: "",
    deadline: "",
    target: "",
    points: "",
    xp: "",
    visibility: "participants",
    retroactive: false,
    achievement: "",
  });
  useEffect(() => {
    const abort = new AbortController();
    api("/goals/participants", "GET", undefined, abort.signal)
      .then(setUsers)
      .catch((e) => {
        if (!abort.signal.aborted) setError(e.message);
      });
    return () => abort.abort();
  }, []);
  return (
    <form
      className="progress-card"
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        setError("");
        try {
          await api("/goals", "POST", {
            ...draft,
            start: new Date(draft.start).toISOString(),
            deadline: new Date(draft.deadline).toISOString(),
          });
          onSaved();
        } catch (e) {
          setError((e as Error).message);
        } finally {
          setBusy(false);
        }
      }}
    >
      <h2>Nova meta</h2>
      {error && <Banner kind="danger">{error}</Banner>}
      <Field label="Objetivo">
        <input
          required
          maxLength={120}
          value={draft.name}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
        />
      </Field>
      <div className="progress-level-edit">
        <Field label="Tipo">
          <select
            value={draft.kind}
            onChange={(e) =>
              setDraft({ ...draft, kind: e.target.value, participantIds: [] })
            }
          >
            <option value="collective">Coletiva</option>
            <option value="individual">Individual</option>
          </select>
        </Field>
        <Field label="Indicador">
          <select
            value={draft.indicator}
            onChange={(e) => setDraft({ ...draft, indicator: e.target.value })}
          >
            <option value="demands">Demandas encerradas</option>
            <option value="points">Pontos de atividades confirmadas</option>
          </select>
        </Field>
      </div>
      <fieldset>
        <legend>Participantes</legend>
        {users.map((u) => (
          <label className="progress-check" key={u.id}>
            <input
              type={draft.kind === "individual" ? "radio" : "checkbox"}
              name="participant"
              checked={draft.participantIds.includes(u.id)}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  participantIds:
                    draft.kind === "individual"
                      ? [u.id]
                      : e.target.checked
                        ? [...draft.participantIds, u.id]
                        : draft.participantIds.filter((id) => id !== u.id),
                })
              }
            />
            {u.name}
          </label>
        ))}
      </fieldset>
      <div className="progress-level-edit">
        {[
          ["start", "Início"],
          ["deadline", "Prazo"],
        ].map(([key, label]) => (
          <Field key={key} label={label}>
            <input
              required
              type="datetime-local"
              value={draft[key as "start" | "deadline"]}
              onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
            />
          </Field>
        ))}
      </div>
      <div className="progress-level-edit">
        {[
          ["target", "Alvo"],
          ["points", "Prêmio em pontos"],
          ["xp", "Prêmio em XP"],
        ].map(([key, label]) => (
          <Field key={key} label={label}>
            <input
              required
              inputMode="decimal"
              value={draft[key as "target" | "points" | "xp"]}
              onChange={(e) =>
                setDraft({ ...draft, [key]: e.target.value.replace(",", ".") })
              }
            />
          </Field>
        ))}
      </div>
      <Field label="Quem pode visualizar">
        <select
          value={draft.visibility}
          onChange={(e) => setDraft({ ...draft, visibility: e.target.value })}
        >
          <option value="participants">Participantes e administrador</option>
          <option value="team">Toda a equipe</option>
        </select>
      </Field>
      <Field label="Conquista ao atingir (opcional)">
        <input
          maxLength={120}
          value={draft.achievement}
          onChange={(e) => setDraft({ ...draft, achievement: e.target.value })}
        />
      </Field>
      <label className="progress-check">
        <input
          type="checkbox"
          checked={draft.retroactive}
          onChange={(e) =>
            setDraft({ ...draft, retroactive: e.target.checked })
          }
        />
        Incluir atividades já realizadas no período escolhido
      </label>
      <p className="small muted">
        Visitas, saúde e protocolos ainda não confirmados não entram por
        suposição. A recompensa é concedida somente ao atingir a meta.
      </p>
      <Button type="submit" kind="primary" busy={busy}>
        Criar meta
      </Button>
    </form>
  );
}

export function FeaturedGoalCard({ go }: { go: (page: string) => void }) {
  const [data, setData] = useState<{ items: GoalRow[]; digits: number } | null>(
      null,
    ),
    [error, setError] = useState(false);
  useEffect(() => {
    const abort = new AbortController();
    api<{ items: GoalRow[]; digits: number }>(
      "/goals",
      "GET",
      undefined,
      abort.signal,
    )
      .then(setData)
      .catch(() => {
        if (!abort.signal.aborted) setError(true);
      });
    return () => abort.abort();
  }, []);
  const active = data?.items.find((row) => row.displayState === "active");
  return (
    <section className="home-goal-preview">
      <span className="home-eyebrow">META EM DESTAQUE</span>
      {error ? (
        <p>Não foi possível consultar as metas.</p>
      ) : !data ? (
        <p role="status">Consultando metas…</p>
      ) : active ? (
        <>
          <h2>{active.goal.name}</h2>
          <p>
            {displayFraction(
              active.calculation.progress,
              active.goal.indicator === "demands" ? 0 : data.digits,
            )}{" "}
            de {active.goal.target}{" "}
            {active.goal.indicator === "demands"
              ? "demandas"
              : "pontos elegíveis"}
          </p>
          <p>
            Até {new Date(active.goal.deadline).toLocaleDateString("pt-BR")}
          </p>
          <Button kind="ghost" onClick={() => go("goals")}>
            Ver meta
          </Button>
        </>
      ) : (
        <>
          <h2>Sem meta ativa</h2>
          <p>As metas publicadas para você aparecerão aqui.</p>
          <Button kind="ghost" onClick={() => go("goals")}>
            Ver metas
          </Button>
        </>
      )}
    </section>
  );
}
